import { useMemo } from 'react';
import type { CSSObjectWithLabel } from 'react-select';

import { useGetAllInstructors } from '~entities/instructors';

import Creatable from '~shared/components/select/creatable';
import { defaultClassNames } from '~shared/components/select/helper';
import { cn } from '~shared/shadcn/lib/utils';

import { ALL_INSTRUCTORS_OPTION, type InstructorOption } from '../lib/types';

const instructorSelectStyles = {
  menuPortal: (base: CSSObjectWithLabel) => ({
    ...base,
    zIndex: 100,
  }),
  control: (base: CSSObjectWithLabel) => ({
    ...base,
    minHeight: 40,
    height: 40,
    maxHeight: 40,
  }),
  valueContainer: (base: CSSObjectWithLabel) => ({
    ...base,
    maxHeight: 38,
    paddingTop: 0,
    paddingBottom: 0,
    overflow: 'hidden' as const,
  }),
};

interface RoomOverviewInstructorFilterProps {
  value: Array<InstructorOption>;
  onChange: (value: Array<InstructorOption>) => void;
}

export function RoomOverviewInstructorFilter({
  value,
  onChange,
}: RoomOverviewInstructorFilterProps) {
  const { data, isFetching } = useGetAllInstructors();

  const instructorOptions = useMemo(
    () =>
      data?.map((instructor) => ({
        label: instructor.fieldName,
        value: instructor._id,
      })) ?? [],
    [data],
  );

  const options = useMemo(
    () => [ALL_INSTRUCTORS_OPTION, ...instructorOptions],
    [instructorOptions],
  );

  const handleChange = (selected: unknown) => {
    const next = (selected as Array<InstructorOption> | null | undefined) ?? [];

    if (next.length === 0) {
      onChange([ALL_INSTRUCTORS_OPTION]);
      return;
    }

    const includesAll = next.some(
      (option) => option.value === ALL_INSTRUCTORS_OPTION.value,
    );
    const previouslyAll = value.some(
      (option) => option.value === ALL_INSTRUCTORS_OPTION.value,
    );

    if (includesAll && next.length > 1) {
      if (previouslyAll) {
        onChange(
          next.filter(
            (option) => option.value !== ALL_INSTRUCTORS_OPTION.value,
          ),
        );
        return;
      }

      onChange([ALL_INSTRUCTORS_OPTION]);
      return;
    }

    onChange(next);
  };

  return (
    <Creatable
      value={value}
      onChange={handleChange}
      isMulti
      isClearable={false}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      placeholder='Select Instructors'
      options={options}
      isLoading={isFetching}
      className='w-full'
      classNames={{
        ...defaultClassNames,
        control: (state) =>
          cn(defaultClassNames.control?.(state), '!h-10 !max-h-10 !min-h-10'),
      }}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition='fixed'
      styles={instructorSelectStyles}
    />
  );
}
